import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useGetSftpSystemSettingsQuery, useUpdateSftpSystemSettingsMutation } from '@/services/sftpSystemSettingsApi';
import { showMutationErrors, handleMutationSuccess, isMutationSuccessful } from '@/utils/errorHandling';
import { toast } from 'react-toastify';
// Time format utilities not needed - API handles human-readable strings directly

export const SftpSystemSettings: React.FC = () => {
  const { data: settings, isLoading, error } = useGetSftpSystemSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateSftpSystemSettingsMutation();
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    enabled: false,
    timerPeriodString: '',
  });
  const [humanReadableTimerPeriod, setHumanReadableTimerPeriod] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings && !isEditMode) {
      // Only update form data when not in edit mode to preserve user input during errors
      setFormData({
        enabled: settings.enabled,
        timerPeriodString: settings.timerPeriodString || '',
      });
      // Set human-readable format (the utility will detect if it's already human-readable)
      setHumanReadableTimerPeriod(settings.timerPeriodString || '');
    }
  }, [settings, isEditMode]);

  const handleFieldChange = (field: string, value: any) => {
    if (field === 'timerPeriodString') {
      // Handle human-readable input directly - no conversion needed
      setHumanReadableTimerPeriod(value);
      setFormData(prev => ({ ...prev, timerPeriodString: value }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Don't use unwrap() as it throws on GraphQL errors
    const response = await updateSettings(formData);
    
    if ('data' in response && response.data) {
      const result = response.data;
      
      if (isMutationSuccessful(result)) {
        handleMutationSuccess('SFTP system settings updated successfully');
        setIsEditMode(false);
        setHasChanges(false);
      } else {
        // Show errors but keep the form in edit mode with user's input
        showMutationErrors(result);
        // Don't reset the form - let user fix the error
      }
    } else if ('error' in response) {
      // Handle network or unexpected errors
      console.error('Failed to update SFTP system settings:', response.error);
      toast.error('Failed to update settings. Please try again.');
    }
  };

  const handleCancel = () => {
    if (settings) {
      setFormData({
        enabled: settings.enabled,
        timerPeriodString: settings.timerPeriodString || '',
      });
      setHumanReadableTimerPeriod(settings.timerPeriodString || '');
    }
    setHasChanges(false);
    setIsEditMode(false);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">Failed to load SFTP system settings</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="SFTP System Settings"
        action={
          !isEditMode && (
            <IconButton onClick={handleEdit} color="primary">
              <EditIcon />
            </IconButton>
          )
        }
      />
      <CardContent>
        {!isEditMode ? (
          <Box display="flex" alignItems="center" gap={3}>
            <Typography component="span" color="text.secondary">
              <strong>Status:</strong> {formData.enabled ? 'Enabled' : 'Disabled'}
            </Typography>
            <Typography component="span" color="text.secondary">
              <strong>Timer Period:</strong> {formData.timerPeriodString}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Enabled</InputLabel>
                <Select
                  value={formData.enabled}
                  label="Enabled"
                  onChange={(e) => handleFieldChange('enabled', e.target.value === 'true')}
                >
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Timer Period"
                value={humanReadableTimerPeriod}
                onChange={(e) => handleFieldChange('timerPeriodString', e.target.value)}
                helperText="Enter time like: 10 minutes, 1 hour, 2 days 6 hours"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!hasChanges || isUpdating}
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};